/**
 * layout — the PLAYBOOK page's binding of the shared detail-page surface.
 *
 * (This is the playbook — `/playbooks/:id` — not a review SESSION. The
 * session's canvas is a finding and binds its own surface in
 * `Review/surface`.)
 *
 * A playbook has three panes over its canvas:
 *
 *   context   what this playbook is: categories, its source document,
 *             the rule counts — read against whichever tab is open  (left)
 *   access    who this playbook is shared with                        (right)
 *   activity  the audit log                                          (right)
 *
 * NO DETAIL PANE. Editing a rule is the FULL global side panel —
 * `showSidePanel({ type: 'editClauseRule' | 'editGlobalRule' })`, rendering
 * `ClauseRuleFormPanel` / `GlobalRuleFormPanel` through `GlobalSidePanel`.
 * A docked container was tried here and gave the rule forms — a risk slider,
 * tag inputs, a four-up severity picker, several long textareas — less room
 * than they need; the wide overlay is the better editor for them. Since
 * nothing else on this page selects a row, the surface declares no detail
 * pane and carries no selection typing (`useSurfaceWithoutSelection`).
 *
 * WHY ENTITY SCOPE. Same reason as matters and projects: a playbook worked
 * from its clause rules and one worked from its activity want different panes
 * open, and one shared key would make every playbook inherit the arrangement
 * of whichever was opened last. So the playbook uuid is part of the key.
 *
 * NO READER PANE. The source document opens in the global file reader (a
 * converted DOCX or a streamed PDF blob), which is not this surface's to hold
 * yet. A pane that can only open onto nothing is not declared — see the note
 * in `defineDetailSurface`.
 *
 * NO LEGACY KEYS. This surface is new; nothing was ever stored for it.
 */

import {
  PanelBounds,
  PanelLayout as GenericLayout,
  PanelSlot as GenericSlot,
} from '../../shared/surface/panelLayout';
import defineDetailSurface, {
  DetailPaneConfig,
} from '../../shared/surface/defineDetailSurface';

export type PlaybookPanelId = 'context' | 'access' | 'activity';

/** The canvas's tabs. Labels (and their counts) are the page's. */
export type PlaybookTab = 'Details' | 'Clause Rules' | 'Global Rules' | 'Reviews';

export const PLAYBOOK_TABS: PlaybookTab[] = [
  'Details',
  'Clause Rules',
  'Global Rules',
  'Reviews',
];

export type PlaybookPanelSlot = GenericSlot<PlaybookPanelId>;
export type PlaybookPanelLayout = GenericLayout<PlaybookPanelId>;

export const PLAYBOOK_PANEL_IDS: PlaybookPanelId[] = [
  'context',
  'access',
  'activity',
];

const PLAYBOOK_PANES: Record<PlaybookPanelId, DetailPaneConfig> = {
  /* Open on a first visit, because these are the facts the Details tab used
     to show in its right column — and the whole point of moving them is that
     they stay readable while the canvas is on Clause Rules. */
  context: {
    label: 'Context',
    bounds: { min: 260, max: 720 },
    dock: 'left', index: 0, width: 320, visible: true, rail: true,
  },
  /* Who a playbook is shared with was reachable only as the header's
     "Manage Access" — the write panel, with no way to simply read the list.
     A playbook is shared across a review team, so "can this person run a
     session against it" is asked far more often than it is changed. */
  access: {
    label: 'Access',
    bounds: { min: 240, max: 640 },
    dock: 'right', index: 0, width: 300, visible: false, rail: true,
  },
  activity: {
    label: 'Activity',
    bounds: { min: 280, max: 760 },
    dock: 'right', index: 1, width: 340, visible: false, rail: true,
  },
};

export const PLAYBOOK_SURFACE = defineDetailSurface<PlaybookPanelId, PlaybookTab>({
  kind: 'detail',
  // Also the `data-testid` prefix for the shell: `playbook-surface`, etc.
  name: 'playbook',
  scope: 'entity',
  ids: PLAYBOOK_PANEL_IDS,
  panes: PLAYBOOK_PANES,
  tabs: PLAYBOOK_TABS,
});

/* ── derived, for call sites and tests that want the plain records ── */

export const PLAYBOOK_PANEL_LABELS: Record<PlaybookPanelId, string> = PLAYBOOK_SURFACE.labels;
export const PLAYBOOK_PANEL_BOUNDS: Record<PlaybookPanelId, PanelBounds> = PLAYBOOK_SURFACE.bounds;
export const PLAYBOOK_DEFAULT_LAYOUT: PlaybookPanelLayout = PLAYBOOK_SURFACE.defaults;
